import { Link } from '@tanstack/react-router';

import { useAuditActions } from '@/shared/api/admin-audit';
import { useUserActivity } from '@/shared/api/admin-overview';
import { type AdminUser, useUserTeams } from '@/shared/api/admin-users';
import { fmtAgo, fmtDate, fmtDateTime } from '@/shared/lib/format';
import { Badge, Card, CopyId, DataRow, Muted } from '@/shared/ui/kit';
import { errorMessage } from '@/shared/lib/ui-utils';

export function AccountCard({ user }: { user: AdminUser }) {
  return (
    <Card title="Account">
      <dl className="space-y-1.5">
        <DataRow label="User id" value={<CopyId value={user.id} label="user id" />} />
        <DataRow label="Email" value={<CopyId value={user.email} label="email" />} />
        <DataRow label="Signed up" value={fmtDateTime(user.createdAt)} />
        <DataRow
          label="Email verified"
          value={user.emailVerifiedAt ? fmtDateTime(user.emailVerifiedAt) : <Badge tone="warn">not verified</Badge>}
        />
        <DataRow label="Two-factor" value={user.twoFactorEnabled ? <Badge tone="info">on</Badge> : 'off'} />
        <DataRow
          label="Sign-in"
          value={
            user.isBanned ? (
              <Badge tone="danger">banned {user.bannedAt ? fmtDate(user.bannedAt) : ''}</Badge>
            ) : user.isActive ? (
              'allowed'
            ) : (
              <Badge tone="warn">deactivated</Badge>
            )
          }
        />
        {user.bannedReason ? (
          <DataRow label="Ban reason" value={<span className="text-red-300">{user.bannedReason}</span>} />
        ) : null}
        {user.deletedAt ? <DataRow label="Deleted" value={fmtDateTime(user.deletedAt)} /> : null}
      </dl>
    </Card>
  );
}

export function ActivityCard({ id }: { id: string }) {
  const { data, isLoading, error } = useUserActivity(id);
  return (
    <Card title="Activity">
      {isLoading ? (
        <Muted>Loading…</Muted>
      ) : error ? (
        <Muted>{errorMessage(error)}</Muted>
      ) : data ? (
        <dl className="space-y-1.5">
          <DataRow
            label="Last seen"
            value={<span title={fmtDateTime(data.lastSeenAt)}>{fmtAgo(data.lastSeenAt)}</span>}
          />
          <DataRow label="Signed-in sessions" value={data.activeSessions} />
          <DataRow
            label="Last action"
            value={<span title={fmtDateTime(data.lastActionAt)}>{fmtAgo(data.lastActionAt)}</span>}
          />
          <DataRow label="Actions, 30 days" value={data.actions30d.toLocaleString()} />
          <p className="pt-1 text-[11px] text-neutral-600">
            Last seen = last token refresh of a live session (empty after sign-out everywhere). Actions = tasks,
            projects and team changes.
          </p>
        </dl>
      ) : null}
    </Card>
  );
}

export function PlanCard({ user }: { user: AdminUser }) {
  return (
    <Card
      title="Personal plan"
      actions={
        <Link
          to="/subscriptions"
          className="text-xs text-orange-400 hover:underline"
        >
          Subscriptions →
        </Link>
      }
    >
      <dl className="space-y-1.5">
        <DataRow label="Plan" value={user.personalPlanCode ?? <span className="text-neutral-500">free</span>} />
        <DataRow label="Status" value={user.personalSubscriptionStatus} />
        <DataRow
          label="Paid until"
          value={user.personalCurrentPeriodEnd ? fmtDate(user.personalCurrentPeriodEnd) : '—'}
        />
      </dl>
      <p className="mt-3 text-xs text-neutral-500">
        Team plans are granted on the team page. Use “Grant plan” above for a personal plan.
      </p>
    </Card>
  );
}

export function TeamsCard({ id, fallbackCount }: { id: string; fallbackCount: number }) {
  const { data: teams, error } = useUserTeams(id);
  return (
    <Card title={`Teams (${teams?.length ?? fallbackCount})`}>
      {error ? (
        <Muted>{errorMessage(error)}</Muted>
      ) : teams === undefined ? (
        <Muted>Loading…</Muted>
      ) : teams.length === 0 ? (
        <Muted>Not a member of any team.</Muted>
      ) : (
        <ul className="divide-y divide-neutral-800">
          {teams.map((t) => (
            <li key={t.teamId} className="flex items-center justify-between gap-2 py-1.5 text-sm">
              <Link
                to="/teams/$id"
                params={{ id: t.teamId }}
                className="min-w-0 truncate text-neutral-200 hover:text-orange-400 hover:underline"
              >
                {t.teamName}
              </Link>
              <span className="flex shrink-0 items-center gap-1.5">
                {!t.isActive ? <Badge tone="warn">left</Badge> : null}
                <span className="text-xs text-neutral-500">{t.rolePreset ?? 'custom'}</span>
                <span className="hidden text-xs text-neutral-600 sm:inline">since {fmtDate(t.joinedAt)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function AdminHistoryCard({ id }: { id: string }) {
  const { data: audit, error } = useAuditActions({ targetType: 'user', targetId: id, hideReads: true, limit: 8 });
  return (
    <Card
      title="Admin actions on this user"
      actions={
        <Link to="/audit" search={{ target: 'user', targetId: id }} className="text-xs text-orange-400 hover:underline">
          Full log →
        </Link>
      }
    >
      {error ? (
        <Muted>{errorMessage(error)}</Muted>
      ) : audit === undefined ? (
        <Muted>Loading…</Muted>
      ) : audit.items.length === 0 ? (
        <Muted>No operator actions recorded on this user.</Muted>
      ) : (
        <ul className="space-y-2">
          {audit.items.map((a) => (
            <li key={a.id} className="text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <code className="text-xs text-orange-400">{a.actionKind}</code>
                <span className="shrink-0 text-xs text-neutral-500" title={fmtDateTime(a.occurredAt)}>
                  {fmtAgo(a.occurredAt)}
                </span>
              </div>
              <div className="text-xs text-neutral-500">
                by {a.actorEmail ?? `${a.actorUserId.slice(0, 8)}…`}
                {a.reason ? <> — {a.reason}</> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
